import { v4 as uuidv4 } from "uuid";
import {
  getChannel,
  getUtmSource,
  getUtmMedium,
  getUtmCampaign,
  getUtmContent,
  getOfferId,
} from "../lib/utils";
import { sendEvent, sendPublicEvent } from "../api/loop";

const UTM_TERM_BY_STATUS = {
  CREATED: "adesao",
  ACTIVATED: "recorrente",
};

const getUtmTerm = () => {
  try {
    // eslint-disable-next-line global-require
    const store = require("../store").default;
    const status = store.getState()?.customerState?.status;
    return UTM_TERM_BY_STATUS[status];
  } catch {
    return undefined;
  }
};

// Atribuicao de campanha (capturada no login digisession do CLARO_YOUTUBE, sagas/session.js).
// "utm_source" ja existe como tag fixa ("frontend_claro_recarga", identifica a origem como o
// app, nao a campanha) - por isso o valor real de campanha vai em "campaign_utm_source" em vez
// de disputar a mesma chave.
const getCampaignTags = () => {
  const utmSource = getUtmSource();
  const utmMedium = getUtmMedium();
  const utmCampaign = getUtmCampaign();
  const utmContent = getUtmContent();
  const offerId = getOfferId();

  return {
    ...(utmSource && { campaign_utm_source: utmSource }),
    ...(utmMedium && { utm_medium: utmMedium }),
    ...(utmCampaign && { utm_campaign: utmCampaign }),
    ...(utmContent && { utm_content: utmContent }),
    ...(offerId && { offer_id: offerId }),
  };
};

const flattenEventData = (eventData, prefix = "event_data_") => {
  if (!eventData || typeof eventData !== "object") {
    return {};
  }

  const flattened = {};

  Object.entries(eventData).forEach(([key, value]) => {
    const newKey = `${prefix}${key}`;

    if (value === null || value === undefined) {
      flattened[newKey] = value;
    } else if (typeof value === "object") {
      flattened[newKey] = JSON.stringify(value);
    } else {
      flattened[newKey] = String(value);
    }
  });

  return flattened;
};

const normalizeTags = tags =>
  Object.fromEntries(
    Object.entries(tags).map(([k, v]) => [k, typeof v === "string" ? v.toLowerCase() : v])
  );

const mountLoopEventData = ({ type, msisdn, sessionLoopId, channelName, flattenedEventData }) => ({
  type,
  msisdn,
  session_id: sessionLoopId,
  tags: {
    channel: "WEB",
    portal: channelName,
    realm: "claro_recarga",
    utm_source: "frontend_claro_recarga",
    ...flattenedEventData,
  },
});

const SESSION_LOOP_ID_KEY = "sessionLoopId";

const getSessionLoopId = () => {
  let id = sessionStorage.getItem(SESSION_LOOP_ID_KEY);
  if (!id) {
    id = uuidv4();
    sessionStorage.setItem(SESSION_LOOP_ID_KEY, id);
  }
  return id;
};

const setSessionLoopId = id => {
  if (!id) return;
  sessionStorage.setItem(SESSION_LOOP_ID_KEY, id);
};

const dispatchLoopEvent = (type, extraData = {}, { identifier, transactionAmount }) => {
  const utmTerm = getUtmTerm();
  const tags = {
    channel: "web",
    portal: getChannel(),
    realm: "claro_recarga",
    utm_source: "frontend_claro_recarga",
    ...(utmTerm && { utm_term: utmTerm }),
    ...getCampaignTags(),
    ...extraData,
  };
  if (transactionAmount !== undefined) tags.transaction_amount = String(transactionAmount);
  sendEvent(identifier, {
    type,
    msisdn: identifier,
    session_id: getSessionLoopId(),
    tags: normalizeTags(tags),
  }).catch(() => {});
};

const dispatchLoopEventCheckout = (type, extraData = {}, { identifier, transactionAmount }) => {
  const utmTerm = getUtmTerm();
  const flattenedEventData = {
    ...(utmTerm && { utm_term: utmTerm }),
    ...getCampaignTags(),
    ...extraData,
  };
  if (transactionAmount !== undefined)
    flattenedEventData.transaction_amount = String(transactionAmount);
  const eventBody = mountLoopEventData({
    type,
    msisdn: identifier,
    sessionLoopId: getSessionLoopId(),
    channelName: getChannel(),
    flattenedEventData: normalizeTags(flattenedEventData),
  });
  sendEvent(identifier, eventBody).catch(() => {});
};

const dispatchPublicLoopEvent = (type, extraData = {}, { identifier, transactionAmount }) => {
  const tags = {
    channel: "web",
    portal: getChannel(),
    realm: "claro_recarga",
    utm_source: "frontend_claro_recarga",
    ...getCampaignTags(),
    ...extraData,
  };
  if (transactionAmount !== undefined) tags.transaction_amount = String(transactionAmount);
  sendPublicEvent({
    type,
    msisdn: identifier,
    session_id: getSessionLoopId(),
    tags: normalizeTags(tags),
  }).catch(() => {});
};

const buildConversionData = (isEligible, hasIntention) => {
  if (isEligible && hasIntention) return { intention_recurrence: true };
  if (isEligible) return { eligible_recurrence: true };
  return {};
};

export {
  flattenEventData,
  mountLoopEventData,
  getSessionLoopId,
  setSessionLoopId,
  dispatchLoopEvent,
  dispatchLoopEventCheckout,
  dispatchPublicLoopEvent,
  buildConversionData,
};
